import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import TrendingUp from '@material-ui/icons/TrendingUp';
import WorkOutline from '@material-ui/icons/WorkOutline';
import AccountBalanceWallet from '@material-ui/icons/AccountBalanceWallet';
import PieChartIcon from '@material-ui/icons/PieChart';
import BarChartIcon from '@material-ui/icons/BarChart';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import CloseIcon from '@material-ui/icons/Close';
import Chart from 'react-apexcharts';

import { dashbordStatsRequest } from '../../actions';
import Loader from '../../components/Loader/Loader';
import api from '../../services';
import { canManageOrganization } from '../../utils/userRoles';

function formatInr(n) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number.isFinite(n) ? n : 0);
}

function isoToDateInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const z = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}`;
}

const ACCENT = {
  primary: '#3949AB',
  teal: '#00897B',
  coral: '#E64A19',
  violet: '#7E57C2',
  amber: '#FFA726',
  slate: '#546E7A',
};

const CHART_COLORS = [
  '#3949AB', '#00897B', '#E64A19', '#7E57C2',
  '#42A5F5', '#FF7043', '#26A69A', '#AB47BC', '#78909C',
];

const useStyles = makeStyles((theme) => ({
  root: {
    marginLeft: 130,
    marginTop: 56,
    padding: theme.spacing(0, 2, 4),
    maxWidth: 1400,
    [theme.breakpoints.down('sm')]: {
      marginLeft: theme.spacing(2),
      padding: theme.spacing(1),
    },
  },
  hero: {
    background: `linear-gradient(135deg, ${ACCENT.primary} 0%, #1A237E 55%, #0D1642 100%)`,
    color: theme.palette.common.white,
    borderRadius: 16,
    padding: theme.spacing(3, 3, 2.5),
    marginBottom: theme.spacing(3),
    boxShadow: '0 12px 40px rgba(26, 35, 126, 0.35)',
  },
  heroTitle: {
    fontWeight: 700,
    letterSpacing: '-0.02em',
    fontSize: '1.75rem',
    [theme.breakpoints.down('xs')]: { fontSize: '1.35rem' },
  },
  heroSubtitle: {
    opacity: 0.92,
    marginTop: theme.spacing(0.75),
    fontSize: '0.95rem',
    maxWidth: 640,
    lineHeight: 1.5,
  },
  filterRow: {
    marginTop: theme.spacing(2),
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    gap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: { flexWrap: 'wrap' },
  },
  filterBtn: {
    color: '#fff',
    borderColor: 'rgba(255,255,255,0.45)',
    textTransform: 'none',
    fontWeight: 500,
    letterSpacing: '0.01em',
    borderRadius: 8,
    '&:hover': {
      borderColor: 'rgba(255,255,255,0.75)',
      backgroundColor: 'rgba(255,255,255,0.08)',
    },
  },
  filterBtnActive: {
    backgroundColor: 'rgba(255,255,255,0.18)',
    color: '#fff',
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 8,
    '&:hover': { backgroundColor: 'rgba(255,255,255,0.26)' },
  },
  dateRangeWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    background: 'rgba(255,255,255,0.10)',
    border: '1px solid rgba(255,255,255,0.22)',
    borderRadius: 10,
    padding: theme.spacing(0.5, 1.5),
    flexShrink: 0,
  },
  dateInput: {
    color: '#fff',
    fontSize: '0.875rem',
    border: 'none',
    background: 'transparent',
    outline: 'none',
    cursor: 'pointer',
    '&::-webkit-calendar-picker-indicator': {
      filter: 'invert(1)',
      opacity: 0.75,
      cursor: 'pointer',
    },
  },
  dateSep: {
    color: 'rgba(255,255,255,0.55)',
    fontSize: '0.75rem',
    userSelect: 'none',
  },
  statCard: {
    borderRadius: 14,
    height: '100%',
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    overflow: 'hidden',
    position: 'relative',
  },
  statCardTint: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
  },
  statIconWrap: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(1.5),
  },
  statLabel: {
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: theme.palette.text.secondary,
    fontWeight: 600,
  },
  statValue: {
    fontSize: '1.65rem',
    fontWeight: 700,
    letterSpacing: '-0.02em',
    color: theme.palette.text.primary,
    marginTop: theme.spacing(0.25),
  },
  chartPaper: {
    borderRadius: 14,
    padding: theme.spacing(2, 2, 1),
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
    height: '100%',
    cursor: 'pointer',
    transition: 'box-shadow 0.15s, border-color 0.15s',
    '&:hover': {
      boxShadow: '0 6px 24px rgba(0,0,0,0.12)',
      borderColor: theme.palette.primary.light,
    },
  },
  chartTitle: {
    fontWeight: 600,
    fontSize: '1rem',
    marginBottom: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  chartHint: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  expandIcon: {
    color: theme.palette.text.secondary,
    padding: 4,
    '&:hover': { color: theme.palette.primary.main },
  },
  modalDialogPaper: {
    borderRadius: 16,
  },
  modalTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: theme.spacing(1),
  },
}));

function StatCard({ classes, tint, iconBg, icon, label, value, sub }) {
  return (
    <Paper className={classes.statCard} elevation={0}>
      <div className={classes.statCardTint} style={{ background: tint }} />
      <Box p={2.5} pt={2}>
        <div className={classes.statIconWrap} style={{ background: iconBg }}>
          {icon}
        </div>
        <Typography className={classes.statLabel}>{label}</Typography>
        <Typography className={classes.statValue}>{value}</Typography>
        {sub ? (
          <Typography variant="caption" color="textSecondary" style={{ marginTop: 8, display: 'block' }}>
            {sub}
          </Typography>
        ) : null}
      </Box>
    </Paper>
  );
}

function ChartCard({ classes, title, hint, icon, onExpand, children }) {
  return (
    <Paper
      className={classes.chartPaper}
      elevation={0}
      onClick={onExpand}
    >
      <Box display="flex" alignItems="flex-start" justifyContent="space-between">
        <Box flex={1}>
          <Typography className={classes.chartTitle}>
            {icon}
            {title}
          </Typography>
          {hint ? <Typography className={classes.chartHint}>{hint}</Typography> : null}
        </Box>
        <Tooltip title="Expand chart">
          <IconButton
            className={classes.expandIcon}
            size="small"
            onClick={(e) => { e.stopPropagation(); onExpand(); }}
          >
            <FullscreenIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      {children}
    </Paper>
  );
}

function ChartModal({ open, onClose, title, hint, children }) {
  const classes = useStyles();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ className: classes.modalDialogPaper }}
    >
      <DialogTitle disableTypography>
        <Box className={classes.modalTitle}>
          <Box>
            <Typography variant="h6" style={{ fontWeight: 700 }}>{title}</Typography>
            {hint ? <Typography variant="body2" color="textSecondary">{hint}</Typography> : null}
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent style={{ paddingBottom: 24 }}>
        {children}
      </DialogContent>
    </Dialog>
  );
}

const Home = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const stats = useSelector((state) => state.Dashboard.dashboardStats);
  const { loader } = useSelector((state) => state.Loader);
  const [filterMode, setFilterMode] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [showOrgSetupBanner, setShowOrgSetupBanner] = useState(false);
  const [expandedChart, setExpandedChart] = useState(null);

  useEffect(() => {
    dispatch(dashbordStatsRequest({}));
  }, [dispatch]);

  useEffect(() => {
    if (!canManageOrganization()) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/tenant/settings');
        if (!cancelled && data && data.onboardingCompleted === false) {
          setShowOrgSetupBanner(true);
        }
      } catch (e) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const fullDashboard = stats && stats.dashboardScope === 'full';
  const ready = stats && stats.period;

  const jobsBarOptions = useMemo(() => {
    if (!ready) return {};
    const categories = stats.jobsByMonth.map((x) => x.month);
    return {
      chart: { fontFamily: 'Roboto, Helvetica, Arial, sans-serif', toolbar: { show: false }, zoom: { enabled: false } },
      colors: [ACCENT.primary],
      plotOptions: { bar: { borderRadius: 8, columnWidth: '55%' } },
      dataLabels: { enabled: false },
      xaxis: { categories, labels: { style: { colors: '#546E7A', fontSize: '11px' } } },
      yaxis: { labels: { style: { colors: '#546E7A' } }, title: { text: 'Jobs' } },
      grid: { borderColor: '#ECEFF1', strokeDashArray: 4 },
      tooltip: { theme: 'light' },
    };
  }, [ready, stats]);

  const jobsBarSeries = useMemo(() => {
    if (!ready) return [];
    return [{ name: 'Jobs', data: stats.jobsByMonth.map((x) => x.count) }];
  }, [ready, stats]);

  const revenueAreaOptions = useMemo(() => {
    if (!ready || !fullDashboard) return {};
    const categories = stats.revenueByMonth.map((x) => x.month);
    return {
      chart: { fontFamily: 'Roboto, Helvetica, Arial, sans-serif', toolbar: { show: false }, zoom: { enabled: false } },
      colors: [ACCENT.teal],
      stroke: { curve: 'smooth', width: 3 },
      fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.45, opacityTo: 0.05 } },
      dataLabels: { enabled: false },
      xaxis: { categories, labels: { style: { colors: '#546E7A', fontSize: '11px' } } },
      yaxis: { labels: { style: { colors: '#546E7A' }, formatter: (val) => formatInr(val) }, title: { text: 'Revenue' } },
      grid: { borderColor: '#ECEFF1', strokeDashArray: 4 },
      tooltip: { y: { formatter: (val) => formatInr(val) } },
    };
  }, [ready, stats, fullDashboard]);

  const revenueAreaSeries = useMemo(() => {
    if (!ready || !fullDashboard) return [];
    return [{ name: 'Revenue', data: stats.revenueByMonth.map((x) => x.total) }];
  }, [ready, stats, fullDashboard]);

  const kpiBarOptions = useMemo(() => {
    if (!ready || !fullDashboard) return {};
    return {
      chart: { fontFamily: 'Roboto, Helvetica, Arial, sans-serif', toolbar: { show: false } },
      colors: [ACCENT.primary, ACCENT.coral, ACCENT.teal],
      plotOptions: { bar: { horizontal: false, borderRadius: 10, columnWidth: '42%', distributed: true } },
      legend: { show: false },
      dataLabels: { enabled: true, formatter: (val) => formatInr(val), style: { fontSize: '11px' } },
      xaxis: { categories: ['Revenue', 'Cost', 'Profit'], labels: { style: { colors: '#546E7A' } } },
      yaxis: { labels: { formatter: (val) => formatInr(val), style: { colors: '#546E7A' } } },
      grid: { borderColor: '#ECEFF1', strokeDashArray: 4 },
      tooltip: { y: { formatter: (val) => formatInr(val) } },
    };
  }, [ready, fullDashboard]);

  const kpiBarSeries = useMemo(() => {
    if (!ready || !fullDashboard) return [];
    return [{ name: 'Amount', data: [stats.revenue, stats.cost, stats.profit] }];
  }, [ready, stats, fullDashboard]);

  const destPieOptions = useMemo(() => {
    if (!ready || !fullDashboard) return {};
    return {
      chart: { fontFamily: 'Roboto, Helvetica, Arial, sans-serif', toolbar: { show: false } },
      labels: stats.jobsByDestinationCity.labels,
      colors: CHART_COLORS,
      legend: { position: 'bottom', fontSize: '12px' },
      dataLabels: { enabled: true },
      plotOptions: {
        pie: {
          donut: {
            size: '58%',
            labels: { show: true, total: { show: true, label: 'Jobs', formatter: () => String(stats.totalJobs) } },
          },
        },
      },
      tooltip: { y: { formatter: (val) => `${val} jobs` } },
    };
  }, [ready, stats, fullDashboard]);

  const destPieSeries = useMemo(() => {
    if (!ready || !fullDashboard || !stats.jobsByDestinationCity.values.length) return [];
    return stats.jobsByDestinationCity.values;
  }, [ready, stats, fullDashboard]);

  const originPieOptions = useMemo(() => {
    if (!ready || !fullDashboard) return {};
    return {
      chart: { fontFamily: 'Roboto, Helvetica, Arial, sans-serif', toolbar: { show: false } },
      labels: stats.jobsByOriginCity.labels,
      colors: CHART_COLORS.slice().reverse(),
      legend: { position: 'bottom', fontSize: '12px' },
      dataLabels: { enabled: true },
      plotOptions: { pie: { expandOnClick: true } },
      tooltip: { y: { formatter: (val) => `${val} jobs` } },
    };
  }, [ready, stats, fullDashboard]);

  const originPieSeries = useMemo(() => {
    if (!ready || !fullDashboard || !stats.jobsByOriginCity.values.length) return [];
    return stats.jobsByOriginCity.values;
  }, [ready, stats, fullDashboard]);

  const statusPieOptions = useMemo(() => {
    if (!ready) return {};
    return {
      chart: { fontFamily: 'Roboto, Helvetica, Arial, sans-serif', toolbar: { show: false } },
      labels: ['Ongoing', 'Completed', 'Other'],
      colors: [ACCENT.amber, ACCENT.teal, ACCENT.slate],
      legend: { position: 'bottom' },
      plotOptions: { pie: { donut: { size: '52%' } } },
    };
  }, [ready]);

  const statusPieSeries = useMemo(() => {
    if (!ready) return [];
    return [stats.statusBreakdown.ongoing, stats.statusBreakdown.completed, stats.statusBreakdown.other];
  }, [ready, stats]);

  const periodCaption = () => {
    if (!ready) return '';
    if (stats.period.mode === 'all') return 'All records combined';
    if (stats.period.mode === 'range') return 'Custom date range';
    if (stats.period.mode === 'fy') return `Financial year FY ${stats.period.label}`;
    return '';
  };

  const loadAllData = () => {
    setFilterMode('all');
    dispatch(dashbordStatsRequest({}));
  };

  const openCustomRange = () => {
    if (stats && stats.period) {
      setCustomFrom(isoToDateInput(stats.period.startISO));
      setCustomTo(isoToDateInput(stats.period.endISO));
    }
    setFilterMode('custom');
  };

  const applyCustomRange = () => {
    if (!customFrom || !customTo) return;
    dispatch(dashbordStatsRequest({ from: customFrom, to: customTo }));
  };

  const closeModal = () => setExpandedChart(null);

  return (
    <div className={classes.root}>
      {loader ? <Loader /> : null}

      {showOrgSetupBanner ? (
        <Paper
          elevation={0}
          style={{ marginBottom: 16, padding: '12px 16px', background: '#E3F2FD', border: '1px solid #90CAF9', borderRadius: 12 }}
        >
          <Typography variant="body2" component="span">
            Complete your <strong>organization branding</strong> so PDFs and customer-facing pages match your company.{' '}
            <Button component={Link} to="/onboarding" size="small" variant="contained" color="primary" style={{ marginLeft: 12 }}>
              Run setup wizard
            </Button>
          </Typography>
        </Paper>
      ) : null}

      <Paper className={classes.hero} elevation={0}>
        <Typography className={classes.heroTitle}>Operations dashboard</Typography>
        {!fullDashboard ? (
          <Typography className={classes.heroSubtitle} style={{ opacity: 0.65, fontSize: '0.82rem' }}>
            Revenue and location charts visible to administrators only.
          </Typography>
        ) : null}
        <Box className={classes.filterRow}>
          <Button
            color="inherit"
            size="small"
            variant={filterMode === 'all' ? 'contained' : 'outlined'}
            className={filterMode === 'all' ? classes.filterBtnActive : classes.filterBtn}
            onClick={loadAllData}
          >
            All data
          </Button>
          <Button
            color="inherit"
            size="small"
            variant={filterMode === 'custom' ? 'contained' : 'outlined'}
            className={filterMode === 'custom' ? classes.filterBtnActive : classes.filterBtn}
            onClick={openCustomRange}
          >
            Custom range
          </Button>
          {filterMode === 'custom' ? (
            <Box className={classes.dateRangeWrap}>
              <input
                type="date"
                className={classes.dateInput}
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                style={{ color: '#fff', fontSize: '0.875rem', border: 'none', background: 'transparent', outline: 'none', cursor: 'pointer' }}
              />
              <span className={classes.dateSep} style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.8rem' }}>→</span>
              <input
                type="date"
                className={classes.dateInput}
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                style={{ color: '#fff', fontSize: '0.875rem', border: 'none', background: 'transparent', outline: 'none', cursor: 'pointer' }}
              />
              <Button
                size="small"
                variant="contained"
                disableElevation
                style={{ backgroundColor: '#fff', color: ACCENT.primary, fontWeight: 600, borderRadius: 6, minWidth: 0, padding: '3px 14px', textTransform: 'none' }}
                onClick={applyCustomRange}
                disabled={!customFrom || !customTo}
              >
                Apply
              </Button>
            </Box>
          ) : null}
        </Box>
      </Paper>

      {!ready ? (
        <Typography color="textSecondary">Loading dashboard…</Typography>
      ) : (
        <>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
            {periodCaption()}: <strong>{stats.period.startLabel}</strong> — <strong>{stats.period.endLabel}</strong>
          </Typography>

          <Grid container spacing={2}>
            {fullDashboard ? (
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard
                    classes={classes}
                    tint={ACCENT.primary}
                    iconBg="rgba(57, 73, 171, 0.12)"
                    icon={<AccountBalanceWallet htmlColor={ACCENT.primary} />}
                    label="Total revenue"
                    value={formatInr(stats.revenue)}
                    sub="Invoices in this period (or linked job date in period)"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard
                    classes={classes}
                    tint={ACCENT.teal}
                    iconBg="rgba(0, 137, 123, 0.12)"
                    icon={<TrendingUp htmlColor={ACCENT.teal} />}
                    label="Profit"
                    value={formatInr(stats.profit)}
                    sub={`Cost ${formatInr(stats.cost)}`}
                  />
                </Grid>
              </>
            ) : null}

            <Grid item xs={12} sm={fullDashboard ? 6 : 6} md={fullDashboard ? 3 : 6}>
              <StatCard
                classes={classes}
                tint={ACCENT.violet}
                iconBg="rgba(126, 87, 194, 0.12)"
                icon={<WorkOutline htmlColor={ACCENT.violet} />}
                label="Total jobs"
                value={String(stats.totalJobs)}
                sub={`${stats.statusBreakdown.completed} completed · ${stats.statusBreakdown.ongoing} ongoing`}
              />
            </Grid>
            <Grid item xs={12} sm={fullDashboard ? 6 : 6} md={fullDashboard ? 3 : 6}>
              <StatCard
                classes={classes}
                tint={ACCENT.coral}
                iconBg="rgba(230, 74, 25, 0.12)"
                icon={<BarChartIcon htmlColor={ACCENT.coral} />}
                label="Avg. jobs / month"
                value={
                  stats.avgJobsPerMonth != null
                    ? String(stats.avgJobsPerMonth)
                    : stats.totalJobs
                    ? (stats.totalJobs / Math.max(1, stats.jobsByMonth.length)).toLocaleString('en-IN', { maximumFractionDigits: 1 })
                    : '0'
                }
                sub="Average over months in this period"
              />
            </Grid>

            {/* ── Jobs by month ── */}
            <Grid item xs={12} lg={fullDashboard ? 8 : 12}>
              <ChartCard
                classes={classes}
                title="Jobs by month"
                hint="Volume of jobs whose job date falls in each month of the selected period."
                icon={<BarChartIcon fontSize="small" htmlColor={ACCENT.primary} />}
                onExpand={() => setExpandedChart('jobs')}
              >
                <Chart options={jobsBarOptions} series={jobsBarSeries} type="bar" height={320} />
              </ChartCard>
            </Grid>

            {/* ── Job status ── */}
            <Grid item xs={12} lg={fullDashboard ? 4 : 12}>
              <ChartCard
                classes={classes}
                title="Job status"
                hint={null}
                icon={<PieChartIcon fontSize="small" htmlColor={ACCENT.teal} />}
                onExpand={() => setExpandedChart('status')}
              >
                <Chart options={statusPieOptions} series={statusPieSeries} type="donut" height={320} />
              </ChartCard>
            </Grid>

            {fullDashboard ? (
              <>
                {/* ── Revenue trend ── */}
                <Grid item xs={12}>
                  <ChartCard
                    classes={classes}
                    title="Revenue trend by month"
                    hint="Invoice totals attributed to each month in this period."
                    icon={<TrendingUp fontSize="small" htmlColor={ACCENT.teal} />}
                    onExpand={() => setExpandedChart('revenue')}
                  >
                    <Chart options={revenueAreaOptions} series={revenueAreaSeries} type="area" height={300} />
                  </ChartCard>
                </Grid>

                {/* ── Revenue / Cost / Profit ── */}
                <Grid item xs={12}>
                  <ChartCard
                    classes={classes}
                    title="Revenue · Cost · Profit"
                    hint="Snapshot for the selected period."
                    icon={<BarChartIcon fontSize="small" htmlColor={ACCENT.primary} />}
                    onExpand={() => setExpandedChart('kpi')}
                  >
                    <Chart options={kpiBarOptions} series={kpiBarSeries} type="bar" height={300} />
                  </ChartCard>
                </Grid>

                {/* ── Destination city ── */}
                <Grid item xs={12} md={6}>
                  <ChartCard
                    classes={classes}
                    title="Jobs by destination city"
                    hint="Share of jobs by delivery city (top cities + Other)."
                    icon={<PieChartIcon fontSize="small" htmlColor={ACCENT.violet} />}
                    onExpand={() => setExpandedChart('dest')}
                  >
                    {destPieSeries.length ? (
                      <Chart options={destPieOptions} series={destPieSeries} type="donut" height={360} />
                    ) : (
                      <Typography color="textSecondary" style={{ padding: 24 }}>No city data for jobs in this period.</Typography>
                    )}
                  </ChartCard>
                </Grid>

                {/* ── Origin city ── */}
                <Grid item xs={12} md={6}>
                  <ChartCard
                    classes={classes}
                    title="Jobs by origin city"
                    hint="Where moves start from (pickup city)."
                    icon={<PieChartIcon fontSize="small" htmlColor={ACCENT.coral} />}
                    onExpand={() => setExpandedChart('origin')}
                  >
                    {originPieSeries.length ? (
                      <Chart options={originPieOptions} series={originPieSeries} type="pie" height={360} />
                    ) : (
                      <Typography color="textSecondary" style={{ padding: 24 }}>No city data for jobs in this period.</Typography>
                    )}
                  </ChartCard>
                </Grid>
              </>
            ) : null}
          </Grid>
        </>
      )}

      {/* ── Expanded chart modals ── */}
      <ChartModal
        open={expandedChart === 'jobs'}
        onClose={closeModal}
        title="Jobs by month"
        hint="Volume of jobs whose job date falls in each month of the selected period."
      >
        <Chart options={jobsBarOptions} series={jobsBarSeries} type="bar" height={520} />
      </ChartModal>

      <ChartModal
        open={expandedChart === 'status'}
        onClose={closeModal}
        title="Job status"
        hint={null}
      >
        <Chart options={statusPieOptions} series={statusPieSeries} type="donut" height={500} />
      </ChartModal>

      <ChartModal
        open={expandedChart === 'revenue'}
        onClose={closeModal}
        title="Revenue trend by month"
        hint="Invoice totals attributed to each month in this period."
      >
        <Chart options={revenueAreaOptions} series={revenueAreaSeries} type="area" height={520} />
      </ChartModal>

      <ChartModal
        open={expandedChart === 'kpi'}
        onClose={closeModal}
        title="Revenue · Cost · Profit"
        hint="Snapshot for the selected period."
      >
        <Chart options={kpiBarOptions} series={kpiBarSeries} type="bar" height={520} />
      </ChartModal>

      <ChartModal
        open={expandedChart === 'dest'}
        onClose={closeModal}
        title="Jobs by destination city"
        hint="Share of jobs by delivery city (top cities + Other)."
      >
        {destPieSeries.length ? (
          <Chart options={destPieOptions} series={destPieSeries} type="donut" height={540} />
        ) : (
          <Typography color="textSecondary" style={{ padding: 24 }}>No city data for jobs in this period.</Typography>
        )}
      </ChartModal>

      <ChartModal
        open={expandedChart === 'origin'}
        onClose={closeModal}
        title="Jobs by origin city"
        hint="Where moves start from (pickup city)."
      >
        {originPieSeries.length ? (
          <Chart options={originPieOptions} series={originPieSeries} type="pie" height={540} />
        ) : (
          <Typography color="textSecondary" style={{ padding: 24 }}>No city data for jobs in this period.</Typography>
        )}
      </ChartModal>
    </div>
  );
};

export default withRouter(Home);
